/**
 * PlaceAutoComplete hook
 *
 * This hook can be applied to an `<input>` element and will hook up a google
 * place auto-complete box for easy entry of a delivery address.
 *
 * The following options are supported on this hook and are passed in by putting `data` attributes on the element.
 *
 * - bounds: JSON string of bounds which should have a `north`, `south`, `east`
 *     and `west` property this will be used to prioritize the values that come back
 *     in the search
 *
 * ## Example
 *
      <input
        type="text"
        phx-hook="PlaceAutoComplete"
        id="delivery-address-autocomplete"
        data-bounds={~s({"north":39.8801998,"south":39.6801998,"east":-86.0559357,"west":-86.2559357})}
        value="55 Monument Circle, Indianapolis, IN 46204"
        class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
      />

    ## Handling Events

    This component will send events back to the host LV whenever the address changes.

        %{
          "address_components" => [
            %{"long_name" => "55", "short_name" => "55", "types" => ["street_number"]},
            %{
              "long_name" => "Monument Circle",
              "short_name" => "Monument Cir",
              "types" => ["route"]
            },
            %{
              "long_name" => "Mile Square",
              "short_name" => "Mile Square",
              "types" => ["neighborhood", "political"]
            },
            %{
              "long_name" => "Indianapolis",
              "short_name" => "Indianapolis",
              "types" => ["locality", "political"]
            },
            %{
              "long_name" => "Center Township",
              "short_name" => "Center Township",
              "types" => ["administrative_area_level_3", "political"]
            },
            %{
              "long_name" => "Marion County",
              "short_name" => "Marion County",
              "types" => ["administrative_area_level_2", "political"]
            },
            %{
              "long_name" => "Indiana",
              "short_name" => "IN",
              "types" => ["administrative_area_level_1", "political"]
            },
            %{
              "long_name" => "United States",
              "short_name" => "US",
              "types" => ["country", "political"]
            },
            %{"long_name" => "46204", "short_name" => "46204", "types" => ["postal_code"]}
          ],
          "formatted_address" => "55 Monument Cir, Indianapolis, IN 46204, USA",
          "geometry" => %{
            "location" => %{"lat" => 39.7682784, "lng" => -86.1571382},
            "viewport" => %{
              "east" => -86.1558924697085,
              "north" => 39.7696529802915,
              "south" => 39.7669550197085,
              "west" => -86.15859043029151
            }
          },
          "html_attributions" => [],
          "name" => "55 Monument Cir"
        }

    We have a helper to parse this structure and return a more friendly format in
    the project. See the `ScratchWeb.GooglePlaceHandler` module for more info.

    ## Event Targets

    If you are hosting this hook inside of a LiveComponent and you would like
    the component to get the event callbacks you can use the `data-phxtarget`
    property on the element:

      <input
        type="text"
        phx-hook="PlaceAutoComplete"
        data-phxtarget={@myself}
        ...
      />

 */
const PlaceAutoCompleteHook = {
  mounted() {
    // if the google maps API has not loaded yet, set up a listener and bail out
    if (!window.google) {
      window.addEventListener("google_map_loaded", (e) => {
        this.mounted();
      });
      return;
    }

    this.defaultValue = this.el.value;
    let defaultBounds = null;
    if (this.el.dataset.bounds) {
      try {
        defaultBounds = JSON.parse(this.el.dataset.bounds);
      } catch {}
    }

    const options = {
      bounds: defaultBounds,
      componentRestrictions: { country: "us" },
      fields: ["address_components", "formatted_address", "geometry", "name"],
      strictBounds: false,
      types: [],
    };

    const autocomplete = new google.maps.places.Autocomplete(this.el, options);

    autocomplete.addListener("place_changed", (e) => {
      this._value = this.el.value;
      if (this.el.dataset.phxtarget || this.el.getAttribute("phx-target")) {
        const pushTarget =
          this.el.dataset.phxtarget || this.el.getAttribute("phx-target");
        this.pushEventTo(
          pushTarget,
          "google-place-autocomplete-change",
          autocomplete.getPlace(),
        );
      } else {
        this.pushEvent(
          "google-place-autocomplete-change",
          autocomplete.getPlace(),
        );
      }
    });

    this.el.addEventListener("focus", (e) => {
      this._value = this.el.value;
      this.el.value = null;
    });

    this.el.addEventListener("blur", (e) => {
      if (this._value) {
        this.el.value = this._value;
      }
    });

    /**
     * Prevents the form form submitting when a place is selected by using the
     * arrow keys + <Enter>
     */
    this.el.addEventListener("keydown", (e) => {
      if (e.keyCode === 13) {
        e.preventDefault();
      }
    });
  },
};

export default PlaceAutoCompleteHook;
